import {
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isOnlineEvent } from 'src/app/helpers/misc';
import { LocalizableError } from 'src/app/models/LocalizableError';
import { Session } from 'src/app/models/Session';
import { ActiveEventService } from 'src/app/services/active-event.service';
import { LabelsService } from 'src/app/services/labels.service';
import { environment } from '../../../environments/environment';
import { ImageHelper } from '../../helpers/ImageHelper';
import { Event } from '../../models/Event';
import { User } from '../../models/User';
import {
  EVENT_SERVICE, LABELS_SERVICE, USER_SERVICE,
} from '../../providers/service.providers';
import { EventService } from '../../services/event.service';
import { ScrollService } from '../../services/scroll.service';
import { UserService } from '../../services/user.service';
import { EventCheckoutRoutePaths } from '../eventregistration/eventregistration.component';
import { SessionCartService } from './session-cart/session-cart.service';

@Component({
  selector: 'app-event',
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss']
})
export class EventComponent implements OnInit {
  public EventDetailSections = EventDetailSections;
  public readableEventId: string;
  public event: Event;
  public loggedInUser: User;
  public sessions: Session[];
  public showSessions: boolean;
  public showSpeakers: boolean;
  public showSessionTracks: boolean;
  public showPasses: boolean;
  public selectedSection: EventDetailSections = EventDetailSections.Sessions;
  public isSessionCartCollapsed = false;
  public isSessionCartAllowed = false;
  public isLoadingData = false;
  public error: LocalizableError;

  private defaultImageUrlSelfHosted = 'default-event-image.jpg';
  private defaultImageUrlCrmHosted = 'homehero.jpg';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private scrollService: ScrollService,
    @Inject(EVENT_SERVICE) private eventService: EventService,
    private imageHelper: ImageHelper,
    @Inject(LABELS_SERVICE) public labelsService: LabelsService,
    @Inject(USER_SERVICE) private userService: UserService,
    private sessionCartService: SessionCartService,
    private activeEventService: ActiveEventService
  ) {
  }

  ngOnInit() {
    this.route.queryParamMap.subscribe((paramMap) => {
      this.readableEventId = paramMap.get(
        EventQueryParameterNames.ReadableEventId
      );

      this.getEventAndSessionData().then(() =>
        this.setIsSessionCartAllowed()
      );
    });

    this.userService.getLoggedInUser().subscribe((loggedInUser) => {
      this.loggedInUser = loggedInUser;
    });

    this.eventService.getSessionTracks(this.readableEventId).subscribe(
      (tracks) => {
        this.showSessionTracks = tracks && tracks.length > 0;
      });

    this.eventService.getSpeakers(this.readableEventId).subscribe(
      (speakers) => {
        this.showSpeakers = speakers && speakers.length > 0;
      });

    this.eventService.getPasses(this.readableEventId).subscribe(
      (passes) => {
        const filteredPasses = this.userService.getFilteredPassesByUser(passes, this.loggedInUser);
        this.showPasses = filteredPasses.length > 0;
      });

    this.sessionCartService.isOpen = false;
  }

  public chooseRoute() {
    if (!this.showSessions) {
      if (this.showSessionTracks) {
        this.router.navigate(['event/session-tracks'], {
          queryParams: {id: this.readableEventId},
          replaceUrl: true
        });
      } else if (this.showSpeakers) {
        this.router.navigate(['event/speakers'], {
          queryParams: {id: this.readableEventId},
          replaceUrl: true
        });
      } else if (this.showPasses) {
        this.router.navigate(['event/pass-information'], {
          queryParams: {id: this.readableEventId},
          replaceUrl: true
        });
      }
    } else {
      this.router.navigate(['event/sessions'], {
        queryParams: {id: this.readableEventId},
        replaceUrl: true
      });
    }
  }

  public getBannerImage() {
    if (this.event == null) {
      // This early exit avoids showing placeholder image while event isn't loaded.
      return '';
    }

    if (this.event.image != null) {
      return this.event.image;
    } else {
      if (environment.useRestStack === true) {
        return this.imageHelper.getImageUrl(this.defaultImageUrlSelfHosted);
      } else {
        return this.imageHelper.getImageUrl(this.defaultImageUrlCrmHosted);
      }
    }
  }

  public registerForSessions() {
    if (this.sessionCartService.getSessionCartForEvent(this.readableEventId).length !== 0) {
      this.router.navigate([EventCheckoutRoutePaths.REGISTRATION], {queryParams: {id: this.readableEventId}});
    } else {
      this.registerForAllSessions();
    }
  }

  public canUserRegister() {
    if (this.event.setRegistrationEndDate) {
      return new Date(Date.now()) < new Date(this.event.stopRegistrationDate);
    }
    return true;
  }

  public isMultiDayEvent({startDate, endDate}: Event): boolean {
    const d1 = new Date(startDate).toISOString().substring(0, 10);
    const d2 = new Date(endDate).toISOString().substring(0, 10);
    return d1 !== d2;
  }

  public isOnlineEvent(event: Event): boolean {
    return isOnlineEvent(event);
  }

  private registerForAllSessions() {
    this.sessions.forEach((session) => {
      this.sessionCartService.addSession(this.readableEventId, session.id);
    });

    this.sessionCartService.isOpen = true;
  }

  private async getEventAndSessionData(): Promise<void> {
    try {
      this.event = await this.activeEventService.getEvent(this.readableEventId).toPromise();

      this.sessions = await this.activeEventService.getSessions(this.readableEventId).toPromise();
      this.showSessions = this.sessions && this.sessions.length > 0;
      this.chooseRoute();
      this.scrollService.handleScrollAfterLoad();
    } catch (error) {
      this.error = error;
      console.error(error);
    }
  }

  private async setIsSessionCartAllowed(): Promise<void> {
    const isFreeEvent = await this.activeEventService.isFreeEvent(this.readableEventId);
    const hasSessions = await this.activeEventService.hasEventSessions(this.readableEventId);

    this.isSessionCartAllowed = this.sessionCartService.isSessionCartEnabledForEvent(
      this.event,
      isFreeEvent,
      hasSessions
    );
  }
}

export enum EventQueryParameterNames {
  ReadableEventId = 'id',
  SpeakerId = 'speakerId'
}

export enum EventDetailSections {
  Sessions = 'sessions',
  SessionTracks = 'session-tracks',
  Speakers = 'speakers',
  PassInformation = 'pass-information'
}
